import { useEffect } from 'react'

// Ref-counted so overlapping locks (closing one dropdown while opening the next) cannot
// restore the page styles while another consumer still holds the lock.
let lockCount = 0
let previousStyles = null

function supportsScrollbarGutter() {
  return typeof CSS !== 'undefined'
    && typeof CSS.supports === 'function'
    && CSS.supports('scrollbar-gutter', 'stable')
}

function lockBody() {
  lockCount += 1

  if (lockCount > 1) {
    return
  }

  const { body, documentElement } = document

  previousStyles = {
    overflow: body.style.overflow,
    paddingRight: body.style.paddingRight,
    scrollbarGutter: documentElement.style.scrollbarGutter,
  }

  // Hiding the viewport scrollbar reclaims its width and shifts the whole page sideways,
  // fixed-position overlays (chatbot button, wayfinder) included. Reserving the gutter keeps
  // that space; browsers without scrollbar-gutter (Safari < 18.2) fall back to padding, which
  // holds the document still but still lets fixed elements shift. Neither is wanted when there is
  // no scrollbar to begin with: `stable` on a page that fits the viewport paints an empty track and
  // shifts the content left for as long as the lock holds.
  const scrollbarWidth = window.innerWidth - documentElement.clientWidth

  if (scrollbarWidth > 0) {
    if (supportsScrollbarGutter()) {
      documentElement.style.scrollbarGutter = 'stable'
    } else {
      body.style.paddingRight = `${scrollbarWidth}px`
    }
  }

  body.style.overflow = 'hidden'
}

function unlockBody() {
  lockCount = Math.max(0, lockCount - 1)

  if (lockCount > 0 || !previousStyles) {
    return
  }

  const { body, documentElement } = document

  body.style.overflow = previousStyles.overflow

  // removeProperty rather than assigning '', which jsdom ignores for padding-right.
  if (previousStyles.paddingRight) {
    body.style.paddingRight = previousStyles.paddingRight
  } else {
    body.style.removeProperty('padding-right')
  }

  documentElement.style.scrollbarGutter = previousStyles.scrollbarGutter
  previousStyles = null
}

// Freezes viewport scrolling while `isLocked` is true. Scrollable regions inside the locked
// overlay keep working, since only the viewport's own scroll is disabled.
function useBodyScrollLock(isLocked) {
  useEffect(() => {
    if (!isLocked) {
      return undefined
    }

    lockBody()

    return unlockBody
  }, [isLocked])
}

export default useBodyScrollLock
